import React from 'react'
import { Pie } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title
} from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend, Title)

export default function PieChart({ 
  data, 
  title, 
  height = 300,
  colors = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'],
  loading = false,
  error = null
}) {
  if (loading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Failed to load chart data</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-loading">No data available</div>
      </div>
    )
  }
  // Enforce stable order and colors for known lot statuses to avoid server/order changes
  const statusOrder = ['AVAILABLE', 'SOLD', 'LOCKED']
  const statusColors = { AVAILABLE: '#55754e', SOLD: '#dc2626', LOCKED: '#FFD700' }
  const allAreStatuses = Array.isArray(data) && data.length > 0 && data.every(d => statusOrder.includes(d.label))
  const sortedData = allAreStatuses
    ? statusOrder
        .filter(s => data.some(d => d.label === s))
        .map(s => data.find(d => d.label === s))
    : data

  const backgroundColors = allAreStatuses
    ? sortedData.map(item => statusColors[item.label])
    : colors.slice(0, sortedData.length)

  const borderColors = backgroundColors.map(color => color + '80')

  const chartData = {
    labels: sortedData.map(item => item.label),
    datasets: [
      {
        data: sortedData.map(item => item.value),
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
        hoverOffset: 4
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1200,
      easing: 'easeOutQuart'
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 24,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 16,
          boxHeight: 10,
          font: {
            size: 13,
            weight: '500',
            family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
          },
          // Dynamically color hovered or active (isolated) legend label darker for clarity
          color: (ctx) => {
            const chart = ctx.chart
            const hoverIndex = chart?.$legendHoverIndex
            const isolated = chart?.$isolatedIndex
            return (ctx.index === hoverIndex || ctx.index === isolated) ? '#111827' : '#374151'
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              const hoverIndex = chart.$legendHoverIndex
              const isolated = chart.$isolatedIndex
              return data.labels.map((label, i) => {
                const isHover = hoverIndex === i || isolated === i
                return {
                  text: label,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  strokeStyle: data.datasets[0].borderColor[i],
                  lineWidth: isHover ? 4 : 2,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i
                }
              });
            }
            return [];
          }
        },
        // Improve legend UX: pointer on hover and highlight corresponding slice
        onHover: (event, legendItem, legend) => {
          try { if (event?.native?.target?.style) event.native.target.style.cursor = 'pointer' } catch (_) {}
          const chart = legend.chart
          const index = legendItem.index
          if (typeof index === 'number') {
            chart.$legendHoverIndex = index
            chart.setActiveElements([{ datasetIndex: 0, index }])
            chart.update()
          }
        },
        onLeave: (event, legendItem, legend) => {
          try { if (event?.native?.target?.style) event.native.target.style.cursor = 'default' } catch (_) {}
          const chart = legend.chart
          chart.$legendHoverIndex = undefined
          chart.setActiveElements([])
          chart.update()
        },
        // Clicking a legend item isolates that slice; click again to restore all
        onClick: (event, legendItem, legend) => {
          const chart = legend.chart
          const idx = legendItem.index
          if (typeof idx !== 'number') return
          const total = chart.data.labels.length
          const isolated = chart.$isolatedIndex
          if (isolated === idx) {
            // Restore all slices
            for (let i = 0; i < total; i++) {
              chart.toggleDataVisibility(i, true) // ensure visible
              // Chart.js v4: toggleDataVisibility only takes index; fallback loop to unhide by toggling hidden=false
              chart._hiddenIndices && delete chart._hiddenIndices[i]
            }
            chart.$isolatedIndex = undefined
          } else {
            // Hide all except clicked
            for (let i = 0; i < total; i++) {
              const visible = i === idx
              // Ensure a deterministic visibility: hide others, show clicked
              const currentlyHidden = chart.getDataVisibility(i) === false
              if (visible && currentlyHidden) chart.toggleDataVisibility(i)
              if (!visible && chart.getDataVisibility(i) !== false) chart.toggleDataVisibility(i)
            }
            chart.$isolatedIndex = idx
          }
          chart.update()
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 18,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#111827',
        padding: {
          top: 16,
          bottom: 24
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: true,
        titleFont: {
          size: 13,
          weight: '600',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        bodyFont: {
          size: 12,
          weight: '400',
          family: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        padding: 12,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const label = context.label || ''
            const value = context.parsed
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            return `${label}: ${value} lots (${percentage}%)`
          }
        }
      }
    },
    elements: {
      arc: {
        borderWidth: 3,
        borderColor: '#ffffff'
      }
    },
    cutout: '0%'
  }

  // Runtime plugin to draw a soft background behind hovered legend item
  const plugins = [{
    id: 'legendHoverBackground',
    afterDraw(chart) {
      const legend = chart.legend
      const hoverIndex = chart.$legendHoverIndex
      const isolated = chart.$isolatedIndex
      const targetIndex = (typeof isolated === 'number') ? isolated : hoverIndex
      if (!legend || typeof targetIndex !== 'number') return
      const hitBox = legend.legendHitBoxes?.[targetIndex]
      if (!hitBox) return
      const ctx = chart.ctx
      const padX = 6
      const padY = 4
      const x = hitBox.left - padX
      const y = hitBox.top - padY
      const w = hitBox.width + padX * 2
      const h = hitBox.height + padY * 2
      // Use the slice color as a faint background
      const bg = (chart.data.datasets?.[0]?.backgroundColor?.[targetIndex]) || '#e5e7eb'
      ctx.save()
      ctx.globalAlpha = (typeof isolated === 'number') ? 0.22 : 0.15
      // rounded rect
      const r = 8
      ctx.beginPath()
      ctx.moveTo(x + r, y)
      ctx.lineTo(x + w - r, y)
      ctx.quadraticCurveTo(x + w, y, x + w, y + r)
      ctx.lineTo(x + w, y + h - r)
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
      ctx.lineTo(x + r, y + h)
      ctx.quadraticCurveTo(x, y + h, x, y + h - r)
      ctx.lineTo(x, y + r)
      ctx.quadraticCurveTo(x, y, x + r, y)
      ctx.closePath()
      ctx.fillStyle = bg
      ctx.fill()
      ctx.restore()
    }
  }]

  return (
    <div style={{ height: height, width: '100%' }}>
      <Pie data={chartData} options={options} plugins={plugins} />
    </div>
  )
}
